import React from 'react';
import { langType } from '../../../../src/lang';
import { AcceptBlock, inputBlock, LangBlock, ShowNotify, TextAreaEditor } from '../../global';
import { SystemShared } from '../../../../shared/system';
import { NewsItem } from '../../../../shared/news';
import { WebStorage } from '../../../storage';
import { createEmptyNewsEdit } from './useAdminData';
import { AdminDataTable } from './admin.table';
import {
    AdminButton,
    AdminFormField,
    AdminFormGrid,
    AdminLangTabs,
    AdminPageWrap,
    AdminPreview,
    AdminSection,
} from './admin.ui';

const LANGS: langType[] = ['ru', 'uk', 'en'];

type Props = {
    news: NewsItem[];
    loadNews: () => void;
};

export const AdminNews = ({ news, loadNews }: Props) => {
    const [, , LangString] = LangBlock();
    const [changeNewsPrev, setChangeNewsPrev] = React.useState<langType>('ru');
    const [newsEdit, setNewsEdit] = React.useState<NewsItem>(createEmptyNewsEdit);

    const isValid =
        newsEdit.id &&
        newsEdit.timestamp &&
        !Object.values(newsEdit.data).find((q) => !q.text);

    return (
        <AdminPageWrap>
            <AdminSection title={LangString('AdminNewsTitle')} subtitle={LangString('AdminNewsSubtitle')}>
                <AdminFormGrid>
                    <AdminFormField label={LangString('AdminId')}>
                        {inputBlock(
                            'text',
                            newsEdit.id,
                            (val) => setNewsEdit((old) => ({ ...old, id: val })),
                            LangString('AdminId'),
                        )}
                    </AdminFormField>
                    <AdminFormField label={LangString('AdminTimestamp')}>
                        {inputBlock(
                            'number',
                            newsEdit.timestamp,
                            (val) => setNewsEdit((old) => ({ ...old, timestamp: val })),
                            LangString('AdminTimestamp'),
                        )}
                    </AdminFormField>
                </AdminFormGrid>

                <div>
                    <AdminButton
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                            e.preventDefault();
                            setNewsEdit((old) => ({ ...old, timestamp: SystemShared.timestamp }));
                        }}
                    >
                        {LangString('AdminSetCurrentTimestamp')}
                    </AdminButton>{' '}
                    <a className="admin-link" href="https://www.unixtimestamp.com/" target="_blank" rel="noreferrer">
                        {LangString('AdminTimestampConverter')}
                    </a>
                </div>

                <AdminLangTabs
                    langs={LANGS}
                    active={changeNewsPrev}
                    onSelect={(lang) => setChangeNewsPrev(lang)}
                />

                <div className="admin-editor-layout">
                    <div>
                        <h3>{LangString('AdminImageUrl')}</h3>
                        {LANGS.map((lan) => (
                            <TextAreaEditor
                                key={`img-${lan}`}
                                helps={[]}
                                name={LangString('AdminEditorImgUrl', lan)}
                                onChange={(val) => {
                                    setNewsEdit((old) => {
                                        old.data[lan].img = val;

                                        return { ...old };
                                    });
                                }}
                                value={newsEdit.data[lan].img || ''}
                                onChangeTrigger="onchange"
                            />
                        ))}

                        <h3>{LangString('AdminTitle')}</h3>
                        {LANGS.map((lan) => (
                            <TextAreaEditor
                                key={`title-${lan}`}
                                helps={[]}
                                name={LangString('AdminEditorTitle', lan)}
                                onChange={(val) => {
                                    setNewsEdit((old) => {
                                        old.data[lan].title = val;

                                        return { ...old };
                                    });
                                }}
                                value={newsEdit.data[lan].title || ''}
                                onChangeTrigger="onchange"
                            />
                        ))}

                        <h3>{LangString('AdminText')}</h3>
                        {LANGS.map((lan) => (
                            <TextAreaEditor
                                key={`text-${lan}`}
                                helps={[]}
                                name={LangString('AdminEditorText', lan)}
                                onChange={(val) => {
                                    setNewsEdit((old) => {
                                        old.data[lan].text = val;

                                        return { ...old };
                                    });
                                }}
                                value={newsEdit.data[lan].text || ''}
                                onChangeTrigger="onchange"
                            />
                        ))}
                    </div>
                    <div>
                        <h3>{LangString('AdminPreviewLabel', changeNewsPrev)}</h3>
                        {newsEdit.data[changeNewsPrev].img ? (
                            <img
                                src={newsEdit.data[changeNewsPrev].img}
                                alt=""
                                style={{ maxWidth: '100%', borderRadius: 5, marginBottom: 12 }}
                            />
                        ) : null}
                        <AdminPreview html={newsEdit.data[changeNewsPrev].title || ''} />
                        <AdminPreview html={newsEdit.data[changeNewsPrev].text || ''} />
                    </div>
                </div>

                <AdminButton
                    variant="primary"
                    block
                    disabled={!isValid}
                    onClick={(e) => {
                        e.preventDefault();

                        if (!isValid) {
                            return ShowNotify(LangString('AdminCreateNews'), LangString('AdminRequireAllParams'), 'error');
                        }

                        WebStorage.ApiRequestPost('/api/news/add', {
                            data: JSON.stringify(newsEdit),
                        }).then((res) => {
                            if (res.status) {
                                ShowNotify(LangString('AdminCreateNews'), LangString('AdminSuccess'), 'success');
                                loadNews();
                            } else {
                                ShowNotify(LangString('AdminCreateNews'), res.error_message, 'error');
                            }
                        });
                    }}
                >
                    {LangString('AdminSaveNews')}
                </AdminButton>

                <AdminDataTable
                    data={news}
                    rowKey={(row) => row.id}
                    emptyMessage={LangString('AdminNoNews')}
                    columns={[
                        {
                            key: 'id',
                            label: LangString('AdminId'),
                            primary: true,
                            render: (row) => row.id,
                        },
                        {
                            key: 'timestamp',
                            label: LangString('AdminDate'),
                            render: (row) => SystemShared.timeStampString(row.timestamp),
                        },
                        {
                            key: 'edit',
                            label: LangString('AdminEdit'),
                            render: (row) => (
                                <AdminButton
                                    variant="primary"
                                    size="sm"
                                    onClick={async (e) => {
                                        e.preventDefault();

                                        if (!(await AcceptBlock())) {
                                            return;
                                        }

                                        setNewsEdit(row);
                                    }}
                                >
                                    {LangString('AdminEdit')}
                                </AdminButton>
                            ),
                        },
                        {
                            key: 'remove',
                            label: LangString('AdminRemove'),
                            render: (row) => (
                                <AdminButton
                                    variant="danger"
                                    size="sm"
                                    onClick={async (e) => {
                                        e.preventDefault();

                                        if (!(await AcceptBlock())) {
                                            return;
                                        }

                                        WebStorage.ApiRequest('/api/news/remove', {
                                            newsId: row.id,
                                        }).then((res) => {
                                            if (res.status) {
                                                loadNews();
                                            }
                                        });
                                    }}
                                >
                                    {LangString('AdminRemove')}
                                </AdminButton>
                            ),
                        },
                    ]}
                />
            </AdminSection>
        </AdminPageWrap>
    );
};
